import isEmpty from 'lodash/isEmpty';
import Constants from '~/config/Constants';
import FeatureFlagNames from '~/config/Constants/FeatureFlags';
import GrowthConstants, { FFS_OCCUPATIONS } from '~/config/GrowthConstants';
import featureFlags from '~/services/FeatureFlagsService';
import $store from '~/services/StoreService';
import UserService from '~/services/UserService';
import { getAccountType, isUserFromPaidOrg } from './UserUtils';

function shouldExposeSFUsingFeatureFlag() {
  return featureFlags().isEnabled(Constants.FeatureFlagsTypes.SHARED_FOLDER_ACCESS);
}

export function isRoWSharedLibraryEnabled() {
  return featureFlags().isEnabled('row-shared-lib');
}

export function isRoWOrgPageEnabled() {
  return featureFlags().isEnabled('row-my-org');
}

export function isSubjectTeacher(subject, includeSubjectTags = false) {
  const user = $store().getters['root/userService'];

  const preferredSubjects = user?.contentPreferences?.subjects?.all || [];
  const isPreferredSubject = preferredSubjects.includes(subject);

  const shouldConsiderSubjectTags = includeSubjectTags || preferredSubjects.length === 0;

  if (shouldConsiderSubjectTags) {
    const subjectTags = user?.subjectTags || [];
    const hasSubjectTag = subjectTags.includes(subject);
    return isPreferredSubject || hasSubjectTag;
  }

  return isPreferredSubject;
}

export function isEnglishTeacher() {
  const user = $store().getters['root/userService'];
  const preferredSubjects = user?.contentPreferences?.subjects?.all;
  if (isEmpty(preferredSubjects)) {
    return user?.subjectTags?.includes('English');
  }
  return preferredSubjects?.includes('English');
}
export function isMathTeacher() {
  const user = $store().getters['root/userService'];
  const preferredSubjects = user?.contentPreferences?.subjects?.all;
  if (isEmpty(preferredSubjects)) {
    return user?.subjectTags?.includes('Mathematics');
  }
  return preferredSubjects?.includes('Mathematics');
}

export function isUSEnglishTeacher() {
  return isEnglishTeacher() && isCountryUS();
}

export function isUSMathsTeacher() {
  return isMathTeacher() && isCountryUS();
}

export function isDesktop() {
  return $store().getters['root/isDesktop'];
}

export function shouldExposeCoTeachingUsingFeatureFlag() {
  return featureFlags().isEnabled(Constants.FeatureFlagsTypes.SHOW_CO_TEACHING);
}

export function shouldExposeListMembersInPaidOrgFeatureFlag() {
  return featureFlags().isEnabled(Constants.FeatureFlagsTypes.LIST_PAID_ORG_MEMBERS);
}

export function shouldExcludeFromLibraryLimits() {
  return featureFlags().isEnabled(Constants.FeatureFlagsTypes.EXCLUDE_FROM_LIBRARY_LIMITS);
}

export function shouldExcludeFromSndChurnBanner() {
  return featureFlags().isEnabled(Constants.FeatureFlagsTypes.EXCLUDE_FROM_SND_CHURN_BANNER);
}

export function canAccessSharedFolders() {
  const user = $store().getters['root/userService'];
  if (!user) {
    return false;
  }

  if (user.isCorporateFreeUser) {
    return false;
  }

  if (shouldExposeSFUsingFeatureFlag()) {
    return true;
  }

  if (shareExperienceV1()) {
    return true;
  }

  return false;
}

export function shareExperienceV1() {
  const user = $store().getters['root/userService'];

  if (!user) {
    return false;
  }

  return !isCountryUS() && user?.isTeacher;
}

export function shouldShowTopContentCreator() {
  return featureFlags().isEnabled(Constants.FeatureFlagsTypes.INFLUENTIAL_TEACHER1)
  || featureFlags().isEnabled(Constants.FeatureFlagsTypes.INFLUENTIAL_TEACHER2)
   || featureFlags().isEnabled(Constants.FeatureFlagsTypes.INFLUENTIAL_TEACHER3);
}

export function shouldShowSharedFolderUpsells() {
  const user = new UserService($store().getters['root/user']);

  if (user.isCorporate) {
    return false;
  }

  return !user.isPartOfAnOrganization;
}

export function isCountryUS() {
  const serverData = $store().getters['root/serverData'];
  const isUSA = serverData?.requestCountry;
  return isUSA === 'US';
}

export function isRegionTexas() {
  const serverData = $store().getters['root/serverData'];

  return Boolean(serverData?.region?.includes('TX'));
}

export function shouldHideCT() {
  if (!shouldExposeCoTeachingUsingFeatureFlag()) {
    return true;
  }

  const user = new UserService($store().getters['root/user']);

  return user.isCorporate;
}

export function shouldLockCT() {
  const user = new UserService($store().getters['root/user']);

  return ((user?.paidOrganizations ?? []).length) <= 0 && !shouldHideCT();
}

export function isTeamCreationFlowV2Enabled() {
  const user = new UserService($store().getters['root/user']);
  const isDesktop = $store().getters['root/isDesktop'];
  return isDesktop && !user.isCorporate && featureFlags().isEnabled(Constants.FeatureFlagsTypes.TEAM_CREATION_FLOW_V2);
}

export const isDistrictLibWidgetV2Enabled = () => featureFlags().getFeatureValue(Constants.FeatureFlagsTypes.DISTRICT_LIB_WIDGET_VARIANT) === 'v2';
export function isDistrictLibWidgetEnabled(version) {
  return featureFlags().getFeatureValue(
    Constants.FeatureFlagsTypes.DISTRICT_LIB_WIDGET_VARIANT,
  ) === version;
}

export const showMathStandardsFilter = () => featureFlags().isEnabled(Constants.FeatureFlagsTypes.MATH_STANDARDS_FILTER);

export function isTeamCreationNudgeEnabled() {
  const user = $store().getters['root/user'];
  return isUserFromPaidOrg(user) && featureFlags().isEnabled(Constants.FeatureFlagsTypes.TEAM_CREATION_NUDGE);
}

export const isShareQuizModalV2Enabled = () => featureFlags().isEnabled(Constants.FeatureFlagsTypes.SHARE_QUIZ_MODAL_V2);

export function shouldShowShareQuizModalUserRecommendations() {
  return featureFlags().isEnabled(
    Constants.FeatureFlagsTypes.SHARE_QUIZ_MODAL_USER_RECOMMENDATIONS,
  );
}

export function isDefaultVisibilityOptionOrgEnabled() {
  return featureFlags().isEnabled(
    Constants.FeatureFlagsTypes.QUIZ_CREATION_DEFAULT_VISIBILITY_ORG,
  );
}

export function isRevampedExplorePgEnabled() {
  const user = $store().getters['root/userService'];
  const isDesktop = $store().getters['root/isDesktop'];
  return isDesktop && user.isLoggedIn && (isCountryUS() || isGrowthHome());
}

export function showMyLibraryV2() {
  return featureFlags().isEnabled(
    Constants.FeatureFlagsTypes.MY_LIBRARY_V2,
  );
}

export function shouldEnforceLibraryLimits() {
  if (!showMyLibraryV2()) {
    return false;
  }
  return featureFlags().isEnabled(Constants.FeatureFlagsTypes.LIBRARY_LIMITS);
}

export function showLLBanner() {
  if (!shouldEnforceLibraryLimits()) {
    return false;
  }
  const variant = featureFlags().getFeatureValue(Constants.FeatureFlagsTypes.LIBRARY_LIMITS_BANNER);
  return [
    Constants.FeatureFlagVariants.LIBRARY_LIMITS_BANNER.WITH_BANNER,
    Constants.FeatureFlagVariants.LIBRARY_LIMITS_BANNER.WITH_BANNER_EXP,
  ].includes(variant);
}

export function showLLSidebarUpgradeCTA() {
  if (!shouldEnforceLibraryLimits()) {
    return false;
  }
  const variant = featureFlags().getFeatureValue(Constants.FeatureFlagsTypes.LIBRARY_LIMITS_BANNER);
  return variant === Constants.FeatureFlagVariants.LIBRARY_LIMITS_BANNER.CONTROLLED;
}

export function showAIAnalyze() {
  return featureFlags().isEnabled(Constants.FeatureFlagsTypes.AI_ANALYZE) && (isSubjectTeacher('English', true) || isSubjectTeacher('Mathematics', true));
}

export function showQuickActionsExplorePg() {
  return featureFlags().isEnabled(Constants.FeatureFlagsTypes.EXPLORE_PAGE_QUICK_ACTIONS);
}

export function isDifferentiationEnabled() {
  return featureFlags().getFeatureValue(FeatureFlagNames.DIFFERENTIATION) !== 'DISABLED';
}

function hasUserCompletedSnDTeacherOnboarding() {
  const user = $store().getters['root/userService'];
  return !(user.id
    && user.onboardingState
    && user.onboardingState.completedTill !== Constants.SnDTeacherOnboarding.CompletedTill.COMPLETED
  );
}

export function shouldUseUserReOnboarding() {
  if (!hasUserCompletedSnDTeacherOnboarding()) {
    return false;
  }

  return $store().getters['root/isDesktop'] && isCountryUS();
}

export function shouldUseSignupOnboarding() {
  if (!hasUserCompletedSnDTeacherOnboarding()) {
    return false;
  }

  return $store().getters['root/isDesktop'] && isCountryUS();
}

export function getSchoolsPlanTrialFFVariant() {
  return featureFlags().getFeatureValue(Constants.FeatureFlagsTypes.SCHOOLS_PLAN_TRIAL_VARIANTS);
}

export function showAIQuickActionCards() {
  return featureFlags().isEnabled(Constants.FeatureFlagsTypes.AI_CREATE_ENHANCE)
  && !isBYOCExperiment()
  && isCountryUS()
  && !isEnglishTeacher();
};

export function canShowElaVocabComprehension() {
  return featureFlags().isEnabled(Constants.FeatureFlagsTypes.ELA_VOCAB) && isEnglishTeacher();
}

export function canShowElaComprehensionCreate() {
  if (['COMPREHENSION_CREATE'].includes(featureFlags().getFeatureValue(Constants.FeatureFlagsTypes.ELA_VOCAB_RC_HOMEPAGE_VARIANTS, 'NOT_ELIGIBLE'))
      && isEnglishTeacher()) {
    return true;
  }
  return false;
}

export function canShowElaComprehensionBrowse() {
  return featureFlags().isEnabled(Constants.FeatureFlagsTypes.ELA_COMPREHENSION_BROWSE) && isEnglishTeacher();
}

export function canShowElaComprehensionShortenedExperience() {
  return featureFlags().isEnabled(Constants.FeatureFlagsTypes.ELA_RC_CREATE_FLOW_SHORTENED) && isEnglishTeacher();
}

export function isSndDashboardInMaintenanceMode() {
  return !featureFlags().isEnabled(Constants.FeatureFlagsTypes.SND_DASHBOARD_ENABLED);
}

export function isMigratedConnectionsPageEnabled() {
  const $user = $store().getters['root/userService'];
  const accountType = getAccountType($user);
  const isSnDAccount = accountType === Constants.AccountTypes.SCHOOL_AND_DISTRICT;
  const isDistrictAdmin = $user.organizationRole === Constants.OrganizationRole.ADMIN;
  const isFFEnabled = featureFlags().isEnabled(Constants.FeatureFlagsTypes.MIGRATED_CONNECTIONS_PAGE);
  return $user.id && isSnDAccount && isDistrictAdmin && isFFEnabled;
}

export function isCleverRosteringEnabled() {
  return featureFlags().isEnabled(Constants.FeatureFlagsTypes.CLEVER_ROSTERING_ENABLED);
}

export function isFCBtsEnabled() {
  return featureFlags().isEnabled(FeatureFlagNames.BTS_FC);
}

export function isGrowthHome() {
  return isFCBtsEnabled() || isFCAdminRevampEnabled();
}

/**
 * FF to decide whether Admin V1 revamp is enabled in Focus Countries
 */
export function isFCAdminV1Enabled() {
  return featureFlags().isEnabled(FeatureFlagNames.FC_ADMIN_V1);
}

/**
 * FF to decide whether Admin Home Screen Revamp is enabled in Focus Countries
 */
export function isFCAdminRevampEnabled() {
  const isDesktop = $store().getters['root/isDesktop'];
  const user = $store().getters['root/userService'];

  return isDesktop && user.isLoggedIn && featureFlags().isEnabled(FeatureFlagNames.FC_ADMIN_REVAMP);
}

/**
 * FF to decide whether Admin Home Import Section is enabled in Focus Countries
 */
export function isFCImportSectionEnabled() {
  const isDesktop = $store().getters['root/isDesktop'];
  const user = $store().getters['root/userService'];

  return isDesktop && user.isLoggedIn && featureFlags().isEnabled(FeatureFlagNames.GROWTH_IMPORT_SECTION);
}

/**
 * FF for toggle new growth onboarding flow for focus countries
 */
export function isNewGrowthOnboardingEnabled() {
  return featureFlags().isEnabled(FeatureFlagNames.NEW_GROWTH_ONBOARDING);
}

export function isSkillDiscoveryHomepageRecommendationEnabled() {
  return featureFlags().isEnabled(Constants.FeatureFlagsTypes.SKILL_DISCOVERY_HOMEPAGE_RECOMMENDATION);
}

export function isBookDiscoveryEnabled() {
  const isDesktop = $store().getters['root/isDesktop'];
  return isDesktop && featureFlags().isEnabled(Constants.FeatureFlagsTypes.BOOK_DISCOVERY);
}

export function isBookDiscoveryQualifiersEnabled() {
  return featureFlags().getFeatureValue(Constants.FeatureFlagsTypes.BOOK_DISCOVERY_VARIATIONS, 'NOT_ELIGIBLE') === Constants.BOOK_DISCOVERY_VARIATIONS.ENABLE_QUALIFIERS;
}

export function getSchoolUpSellModalVariant() {
  return featureFlags().getFeatureValue(Constants.FeatureFlagsTypes.SCHOOLS_UPSELL_MODAL_VARIANT);
}

export function enableNativeFormForFC() {
  return featureFlags().isEnabled(GrowthConstants.FeatureFlags.ENABLE_NATIVE_FORM_FC);
}

export function isQuizizzRewindEnabled() {
  const user = $store().getters['root/userService'];
  const isDesktop = $store().getters['root/isDesktop'];
  return featureFlags().isEnabled(Constants.FeatureFlagsTypes.REWIND) && user.isLoggedIn && isDesktop;
}

export function isAIVisionEnabled() {
  return featureFlags().getFeatureValue(Constants.FeatureFlagsTypes.QUIZIZZ_AI_CREATE_OCR_VERSION) === 'v2';
}

export function isCreateAIUsingFileEnabled() {
  return featureFlags().isEnabled(FeatureFlagNames.PROJ_CREATE_AI_FILE_CARD)
  && showAIQuickActionCards()
  && isAIVisionEnabled()
  && !isEnglishTeacher();
}
export function isUpdatedOutOfStorageModalEnabled() {
  const isDesktop = $store().getters['root/isDesktop'];
  return featureFlags().isEnabled(Constants.FeatureFlagsTypes.UPDATED_OUT_OF_STORAGE_MODAL) && isDesktop;
}

export function isPremiumQuizPaywallingRelaxed() {
  const userService = $store().getters['root/userService'];
  return featureFlags().isEnabled(Constants.FeatureFlagsTypes.QUIZ_PAYWALLING_UPDATE) && userService.isBasicUSAccount;
}
export function isBYOCExperiment() {
  return featureFlags().isEnabled(Constants.FeatureFlagsTypes.AI_CREATE_ENHANCE)
  && featureFlags().isEnabled(Constants.FeatureFlagsTypes.AI_BYOC)
  && !isEnglishTeacher()
  && !isMathTeacher();
}

export function isSkillsExpAllowed() {
  return featureFlags().isEnabled(FeatureFlagNames.SKILLS_EXP);
}

export function shouldShowAiStudio() {
  const isDesktop = $store().getters['root/isDesktop'];
  const user = $store().getters['root/userService'];
  return !user.isStudent && isDesktop && featureFlags().isEnabled(FeatureFlagNames.PROJ_AI_STUDIO);
}
export function isUserEligibleForFFS() {
  const user = $store().getters['root/userService'];
  return featureFlags().isEnabled(GrowthConstants.FeatureFlags.FFS_SCHOOL_PAGE)
  && ((FFS_OCCUPATIONS.includes(user.occupation) && !user.isSnDUser && !user.isPremiumUser) || !user.isLoggedIn);
}
export function isUserEligibleForFFS4US() {
  const user = $store().getters['root/userService'];
  return featureFlags().isEnabled(FeatureFlagNames.FREE_FOR_SCHOOLS_US) && !user.isSnDUser && !user.isPremiumUser;
}

export function isRcQtSnDOnly() {
  // RC - reading comprehension
  // QT - question type
  // this util is used to check whether RC is for SnD only
  const user = $store().getters['root/userService'];
  if (user.isComprehensionQTBetaUser) {
    return false;
  }
  return featureFlags().isEnabled(Constants.FeatureFlagsTypes.RC_QT_SND_ONLY) && !user.isSndUser;
}

export function isSuperBrandingConsistencyEnabled() {
  return featureFlags().isEnabled(Constants.FeatureFlagsTypes.BRANDING_ZAP_TO_LOCK);
}

export function isByocOnboardingEnabled() {
  const isDesktop = $store().getters['root/isDesktop'];
  return isDesktop && featureFlags().isEnabled(FeatureFlagNames.BYOC_ONBOARDING);
}

export function showNewClassAssignmentModalWithDifferentiation() {
  return featureFlags().isEnabled(FeatureFlagNames.DIFFERENTIATION_CLASS_ASSIGNMENT);
}

export function showDemoBannerOnSummary() {
  const userService = $store().getters['root/userService'];
  return (userService.isBasicUSAccount
    && featureFlags().getFeatureValue(Constants.FeatureFlagsTypes.DEMO_QUIZ_BANNER, 'NOT_ELIGIBLE') === Constants.DEMO_QUIZ_BANNER.GAME_SUMMARY);
}

export function showDemoBannerOnWaiting() {
  const userService = $store().getters['root/userService'];
  return (userService.isBasicUSAccount
    && featureFlags().getFeatureValue(Constants.FeatureFlagsTypes.DEMO_QUIZ_BANNER, 'NOT_ELIGIBLE') === Constants.DEMO_QUIZ_BANNER.GAME_WAITING);
}
